import React, { ReactNode, useMemo } from 'react';
import Select, {
  components,
  OptionProps,
  GroupBase,
  IndicatorsContainerProps,
  NoticeProps,
  MultiValue,
  Props as SelectProps,
} from 'react-select';
import type { MultiValueProps } from 'react-select';
import { ChevronDown, PackageOpen, X } from 'lucide-react';
import { cn } from '@/lib/utils.ts';
import Option from '@/components/form/types/Option.ts';
import {
  getSelectStyles,
  SelectControlSize,
} from '@/components/form/_primitives/primitiveSelectStyles.ts';
import PrimitiveCheckbox from '@/components/form/_primitives/PrimitiveCheckbox.tsx';
import {
  MOBILE_SELECT_SEARCH_THRESHOLD,
  useMobileSelectMenu,
} from '@/components/form/_primitives/mobileSelectMenu.ts';
import Chip from '@/components/ui/Chip.tsx';

/**
 * Własny wygląd opcji i wybranej wartości — dla list, w których sama nazwa nie
 * wystarcza (barwny tag). `label` zostaje tekstem, bo react-select po nim
 * wyszukuje.
 */
type OptionLabelRenderer = (option: Option) => ReactNode;

type PrimitiveMultiSelectProps = {
  options: Option[];
  value?: (string | object)[];
  onChange?: (value: (string | object)[]) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: string;
  className?: string;
  name?: string;
  id?: string;
  valueObjectComparator?: (
    optionValue: string | object,
    value: string | object,
  ) => boolean;
  renderOptionLabel?: OptionLabelRenderer;
  controlSize?: SelectControlSize;
} & Omit<
  SelectProps<Option, true>,
  'value' | 'onChange' | 'options' | 'isDisabled' | 'isMulti'
>;

const PrimitiveMultiSelect = ({
  options,
  value = [],
  onChange,
  placeholder = 'Wybierz',
  disabled = false,
  error,
  className,
  name,
  id,
  valueObjectComparator,
  renderOptionLabel,
  controlSize = 'default',
  ...rest
}: PrimitiveMultiSelectProps) => {
  const optionValues = useMemo(() => {
    if (!value.length) return [];

    if (valueObjectComparator === undefined) {
      return options.filter((option) => value.some((v) => option.value === v));
    }

    return options.filter((option) =>
      value.some((v) => valueObjectComparator(option.value, v)),
    );
  }, [options, value, valueObjectComparator]);

  const mobileMenu = useMobileSelectMenu({
    title: rest.mobileMenuTitle ?? placeholder,
    searchable: options.length >= MOBILE_SELECT_SEARCH_THRESHOLD,
  });

  const isMobileMenu = mobileMenu !== null;
  const styles = useMemo(
    () =>
      getSelectStyles<true>(error, controlSize, { mobileMenu: isMobileMenu }),
    [error, controlSize, isMobileMenu],
  );

  // Tożsamość podkomponentów musi przetrwać przerysowanie, inaczej react-select
  // montuje listę opcji od nowa przy każdym znaku w wyszukiwarce.
  const selectComponents = useMemo(
    () => ({
      Option: (optionProps: OptionProps<Option, true, GroupBase<Option>>) => (
        <CheckboxOption {...optionProps} renderLabel={renderOptionLabel} />
      ),
      IndicatorsContainer: CustomIndicator,
      NoOptionsMessage: CustomNoOptions,
      MultiValue: (
        multiValueProps: MultiValueProps<Option, true, GroupBase<Option>>,
      ) => (
        <CustomMultiValue
          {...multiValueProps}
          renderLabel={renderOptionLabel}
        />
      ),
    }),
    [renderOptionLabel],
  );

  return (
    <Select<Option, true>
      {...rest}
      name={name}
      inputId={id ?? name}
      placeholder={placeholder}
      options={options}
      classNamePrefix="custom-select"
      isSearchable
      isMulti
      isDisabled={disabled}
      className={className}
      value={optionValues}
      onChange={(selectedOptions: MultiValue<Option>) => {
        onChange?.(selectedOptions.map((opt) => opt.value));
      }}
      closeMenuOnSelect={false}
      hideSelectedOptions={false}
      menuPortalTarget={document.body}
      menuPosition="fixed"
      menuShouldBlockScroll={true}
      components={{ ...selectComponents, ...mobileMenu?.components }}
      styles={styles}
      {...mobileMenu?.selectProps}
    />
  );
};

const CustomMultiValue = ({
  renderLabel,
  ...props
}: MultiValueProps<Option, true, GroupBase<Option>> & {
  renderLabel?: OptionLabelRenderer;
}) => {
  // react-select woła `removeProps.onClick` bez zdarzenia — to samo usunięcie.
  const remove = props.removeProps.onClick as (() => void) | undefined;

  // Własny wygląd jest już pigułką (barwny tag), więc `Chip` dołożyłby drugą ramkę.
  if (renderLabel) {
    return (
      <span className="inline-flex min-w-0 items-center gap-1">
        {renderLabel(props.data)}
        <button
          type="button"
          aria-label={`Usuń ${props.data.label as string}`}
          onClick={remove}
          onMouseDown={(event) => event.stopPropagation()}
          className="shrink-0 text-neutral-400 transition-colors hover:text-neutral-700"
        >
          <X size={12} strokeWidth={2.5} />
        </button>
      </span>
    );
  }

  return (
    <Chip
      // Wielokrotny wybór dostaje etykiety tekstowe; węzeł zdarza się tylko
      // w wyborze pojedynczym.
      label={props.data.label as string}
      fill="soft"
      onRemove={remove}
    />
  );
};

const CheckboxOption = ({
  renderLabel,
  ...props
}: OptionProps<Option, true, GroupBase<Option>> & {
  renderLabel?: OptionLabelRenderer;
}) => {
  return (
    <components.Option {...props}>
      <div className="flex items-center gap-2">
        <PrimitiveCheckbox
          checked={props.isSelected}
          readOnly
          tabIndex={-1}
          className="border-neutral-200"
        />
        <label className="cursor-pointer text-sm leading-4">
          {renderLabel ? renderLabel(props.data) : props.label}
        </label>
      </div>
    </components.Option>
  );
};

const CustomIndicator = (props: IndicatorsContainerProps<Option, true>) => {
  return (
    <div className="p-1 px-2">
      <components.IndicatorsContainer {...props}>
        <ChevronDown
          className={cn('text-neutral-600 hover:text-neutral-900')}
          size={20}
          strokeWidth={2}
        />
      </components.IndicatorsContainer>
    </div>
  );
};

const CustomNoOptions = (props: NoticeProps<Option, true>) => {
  return (
    <components.NoOptionsMessage {...props}>
      <div className="flex flex-row items-start gap-1 text-sm text-neutral-600">
        <PackageOpen className="size-5 min-w-5 text-neutral-400" />
        <p className="truncate">Brak opcji</p>
      </div>
    </components.NoOptionsMessage>
  );
};

export default PrimitiveMultiSelect;
